import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormControl,
    FormLabel,
    Input,
    useToast,
    Text,
    VStack,
    HStack,
    IconButton,
    Flex,
    Checkbox,
    Box,
    InputGroup,
    FormHelperText,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, AttachmentIcon } from "@chakra-ui/icons";
import React, { useState, useRef, useEffect, useMemo } from "react";
import ReportsApi from "../../../../api/reports";

export default function SendInvoiceEmailModal({ isOpen, onClose, report, deductionReportOnlyMode = false }) {
    const toast = useToast();
    const [emailAddresses, setEmailAddresses] = useState([{ value: "" }]);
    const [isSending, setIsSending] = useState(false);
    const [includeDeductionReport, setIncludeDeductionReport] = useState(true);
    const [sendToMoveoOffice, setSendToMoveoOffice] = useState(true);
    const [selectedContactEmails, setSelectedContactEmails] = useState([]);
    const [deductionReportFiles, setDeductionReportFiles] = useState([]);
    const fileInputRef = useRef(null);

    // Function to check if document name contains a specific string
    const hasDocumentWithName = (nameSubstring) => {
        if (!report?.documents || !Array.isArray(report.documents)) {
            return false;
        }
        return report.documents.some(doc =>
            doc.docName && doc.docName.includes(nameSubstring)
        );
    };

    // Parse document types from documentId JSON string
    const getDocumentTypes = () => {
        if (!report?.documentId) return [];

        try {
            const docArray = JSON.parse(report.documentId);
            return docArray.map(doc => doc.type);
        } catch (e) {
            console.error("Error parsing documentId:", e);
            return [];
        }
    };

    const documentTypes = getDocumentTypes();
    const hasEmployeeTransfersFeeInvoice = documentTypes.includes(1) || hasDocumentWithName("חשבונית מס עובדים");
    const hasAdvancePaymentRequest = documentTypes.includes(2) || hasDocumentWithName("דרישת תשלום");
    const hasCompanySubscriptionFeeInvoice = documentTypes.includes(4) || hasDocumentWithName("חשבונית דמי מנוי");

    const parseContactEmails = () => {
        if (!report?.contactEmails) return [];

        if (Array.isArray(report.contactEmails)) {
            // Clean up each email - remove brackets and trim
            return report.contactEmails.map(email => {
                let cleanEmail = email.trim();
                if (cleanEmail.startsWith('[')) {
                    cleanEmail = cleanEmail.slice(1);
                }

                if (cleanEmail.endsWith(']')) {
                    cleanEmail = cleanEmail.slice(0, -1);
                }
                return cleanEmail.trim();
            }).filter(e => e);
        }

        // If it's a string
        if (typeof report.contactEmails === 'string') {
            let emailString = report.contactEmails.trim();

            // Remove surrounding brackets if present: "[email1, email2]" -> "email1, email2"
            if (emailString.startsWith('[') && emailString.endsWith(']')) {
                emailString = emailString.slice(1, -1);
            }

            // Split by comma and clean up
            return emailString.split(',').map(e => e.trim()).filter(e => e);
        }

        return [];
    };

    const contactEmails = useMemo(() => parseContactEmails(), [report?.contactEmails]);

    // Initialize state when modal opens
    useEffect(() => {
        if (isOpen) {
            setIncludeDeductionReport(true);
            setSendToMoveoOffice(true);
            setSelectedContactEmails(contactEmails);
            setDeductionReportFiles([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    }, [isOpen, contactEmails, deductionReportOnlyMode]);

    const handleAddEmail = () => {
        setEmailAddresses([...emailAddresses, { value: "" }]);
    };

    const handleContactEmailToggle = (email) => {
        setSelectedContactEmails(prev =>
            prev.includes(email)
                ? prev.filter(e => e !== email)
                : [...prev, email]
        );
    };

    const handleRemoveEmail = (index) => {
        const updatedEmails = [...emailAddresses];
        updatedEmails.splice(index, 1);
        setEmailAddresses(updatedEmails);
    };

    const handleEmailChange = (index, value) => {
        const updatedEmails = [...emailAddresses];
        updatedEmails[index].value = value;
        setEmailAddresses(updatedEmails);
    };

    const isExcelFile = (file) => {
        if (!file) return false;
        return file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            file.type === "application/vnd.ms-excel" ||
            file.name.toLowerCase().endsWith(".xlsx") ||
            file.name.toLowerCase().endsWith(".xls");
    };

    const handleFileChange = (event) => {
        const incomingFiles = Array.from(event.target.files || []);
        if (!incomingFiles.length) {
            return;
        }

        const validFiles = incomingFiles.filter(isExcelFile);
        const invalidFiles = incomingFiles.filter(file => !isExcelFile(file));

        if (invalidFiles.length > 0) {
            toast({
                title: "סוג קובץ לא תקין",
                description: "יש לבחור קבצי אקסל בלבד (.xlsx או .xls)",
                position: "top",
                status: "error",
                isClosable: true,
            });
        }

        if (validFiles.length > 0) {
            setDeductionReportFiles(prevFiles => {
                const mergedFiles = [...prevFiles, ...validFiles];
                return mergedFiles.filter((file, index, allFiles) =>
                    allFiles.findIndex(existing =>
                        existing.name === file.name &&
                        existing.size === file.size &&
                        existing.lastModified === file.lastModified
                    ) === index
                );
            });
        }

        // Let users re-select the same file(s) on next browse.
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleBrowseClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleRemoveFile = () => {
        setDeductionReportFiles([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSendEmail = () => {
        // Filter out empty email addresses
        let validEmails = emailAddresses
            .map(email => email.value.trim())
            .filter(email => email !== "");

        // Add Moveo office email if checkbox is checked
        if (sendToMoveoOffice) {
            validEmails.push("office@moveo.co.il");
            validEmails.push("amir.nagar@payro.io");
        }

        // Add selected contact emails
        if (selectedContactEmails.length > 0) {
            validEmails.push(...selectedContactEmails);
        }

        if (validEmails.length === 0) {
            toast({
                title: "נא להזין לפחות כתובת אימייל אחת",
                position: "top",
                status: "error",
                isClosable: true,
            });
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidEmails = validEmails.filter(email => !emailRegex.test(email));

        if (invalidEmails.length > 0) {
            toast({
                title: "כתובות אימייל לא תקינות",
                description: invalidEmails.join(", "),
                position: "top",
                status: "error",
                isClosable: true,
            });
            return;
        }

        // Check if deduction report is required but not provided
        if ((deductionReportOnlyMode || includeDeductionReport) && deductionReportFiles.length === 0) {
            toast({
                title: "דו״ח ניכויים חסר",
                description: "סימנת לכלול דו״ח ניכויים, אך לא העלית קבצים",
                position: "top",
                status: "warning",
                isClosable: true,
            });
            return;
        }

        setIsSending(true);

        // Format the pay period to MM/YYYY
        const payPeriodParts = report.payPeriod.split("/");
        let formattedPayPeriod = report.payPeriod;

        // Ensure consistent MM/YYYY format
        if (payPeriodParts.length === 2) {
            // If already in MM/YYYY format, ensure month is 2 digits
            const month = payPeriodParts[0].padStart(2, '0');
            formattedPayPeriod = `${month}/${payPeriodParts[1]}`;
        }

        // Create FormData to send the file
        const formData = new FormData();
        formData.append('emailAddresses', JSON.stringify(validEmails));
        formData.append('companyId', report.companyId);
        formData.append('reportId', report.id);
        formData.append('payPeriod', formattedPayPeriod);
        formData.append('includeDeductionReport', (deductionReportOnlyMode || includeDeductionReport).toString());
        formData.append('deductionReportOnlyMode', deductionReportOnlyMode.toString());

        if (deductionReportOnlyMode || includeDeductionReport) {
            deductionReportFiles.forEach((file) => {
                formData.append('deductionReportFile', file);
            });
        }

        ReportsApi.sendInvoiceEmailWithAttachment(formData)
            .then(response => {
                if (response.data.success) {
                    toast({
                        title: "אימייל נשלח בהצלחה",
                        description: `נשלח ל: ${validEmails.join(", ")}`,
                        position: "top",
                        status: "success",
                        isClosable: true,
                    });
                    setEmailAddresses([{ value: "" }]);
                    setSelectedContactEmails(contactEmails);
                    setDeductionReportFiles([]);
                    onClose();
                } else {
                    toast({
                        title: "שגיאה בשליחת האימייל",
                        description: response.data.error || "אירעה שגיאה בלתי צפויה",
                        position: "top",
                        status: "error",
                        isClosable: true,
                    });
                }
            })
            .catch(error => {
                toast({
                    title: "שגיאה בשליחת האימייל",
                    description: error.message || "אירעה שגיאה בלתי צפויה",
                    position: "top",
                    status: "error",
                    isClosable: true,
                });
            })
            .finally(() => {
                setIsSending(false);
            });
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            isCentered
            closeOnOverlayClick={false}
        >
            <ModalOverlay />
            <ModalContent maxW="500px">
                <ModalHeader>
                    {deductionReportOnlyMode ? "שליחת דו״ח ניכויים במייל" : "שליחת מסמכים במייל"}
                    <Text fontSize="md" fontWeight="normal" mt={2}>
                        {report?.companyName} - {report?.payPeriod}
                    </Text>
                </ModalHeader>
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <Text>
                            {deductionReportOnlyMode
                                ? "הזן את כתובות האימייל אליהן תרצה לשלוח את דו״ח הניכויים:"
                                : "הזן את כתובות האימייל אליהן תרצה לשלוח את המסמכים הבאים:"
                            }
                        </Text>
                        {!deductionReportOnlyMode && hasEmployeeTransfersFeeInvoice && (
                            <Text fontWeight="bold">• חשבונית מס עבור עמלות עובדים</Text>
                        )}
                        {!deductionReportOnlyMode && hasAdvancePaymentRequest && (
                            <Text fontWeight="bold">• דרישת תשלום (עבור מקדמות)</Text>
                        )}
                        {!deductionReportOnlyMode && hasCompanySubscriptionFeeInvoice && (
                            <Text fontWeight="bold">• חשבונית מס עבור דמי מנוי</Text>
                        )}

                        {!deductionReportOnlyMode && (
                            <Box py={2}>
                                <Checkbox
                                    isChecked={includeDeductionReport}
                                    onChange={(e) => {
                                        setIncludeDeductionReport(e.target.checked);
                                        if (!e.target.checked) {
                                            // If unchecked, clear files
                                            setDeductionReportFiles([]);
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = "";
                                            }
                                        }
                                    }}
                                    colorScheme="blue"
                                >
                                    <Text fontWeight="bold">צרף דו״ח ניכויים</Text>
                                </Checkbox>
                            </Box>
                        )}

                        {(deductionReportOnlyMode || includeDeductionReport) && (
                            <FormControl>
                                <FormLabel>העלאת דו״ח ניכויים (XLSX):</FormLabel>
                                <InputGroup>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        accept=".xlsx,.xls"
                                        multiple
                                        style={{ display: 'none' }}
                                        disabled={isSending}
                                    />
                                    <Flex width="100%">
                                        <Button
                                            leftIcon={<AttachmentIcon />}
                                            onClick={handleBrowseClick}
                                            isDisabled={isSending}
                                            mr={2}
                                        >
                                            בחר קובץ
                                        </Button>
                                        <Input
                                            value={deductionReportFiles.length === 0
                                                ? "לא נבחר קובץ"
                                                : deductionReportFiles.length === 1
                                                    ? deductionReportFiles[0].name
                                                    : `${deductionReportFiles.length} קבצים נבחרו`
                                            }
                                            isReadOnly
                                            placeholder="לא נבחר קובץ"
                                        />
                                        {deductionReportFiles.length > 0 && (
                                            <IconButton
                                                icon={<DeleteIcon />}
                                                onClick={handleRemoveFile}
                                                aria-label="Remove files"
                                                colorScheme="red"
                                                ml={2}
                                                isDisabled={isSending}
                                            />
                                        )}
                                    </Flex>
                                </InputGroup>
                                <FormHelperText>ניתן לבחור קובץ אחד או יותר מסוג אקסל (.xlsx או .xls)</FormHelperText>
                            </FormControl>
                        )}

                        <Box py={2}>
                            <Checkbox
                                isChecked={sendToMoveoOffice}
                                onChange={(e) => setSendToMoveoOffice(e.target.checked)}
                                colorScheme="blue"
                            >
                                <Text>שלח גם ל-office@moveo.co.il ול-amir.nagar@payro.io</Text>
                            </Checkbox>
                        </Box>

                        {contactEmails && contactEmails.length > 0 && (
                            <Box py={2} borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                                <Text fontWeight="bold" mb={2}>אנשי קשר של החברה:</Text>
                                <VStack align="stretch" spacing={2}>
                                    {contactEmails.map((email, index) => (
                                        <Checkbox
                                            key={index}
                                            isChecked={selectedContactEmails.includes(email)}
                                            onChange={() => handleContactEmailToggle(email)}
                                            colorScheme="blue"
                                            isDisabled={isSending}
                                        >
                                            <Text>{email}</Text>
                                        </Checkbox>
                                    ))}
                                </VStack>
                            </Box>
                        )}

                        <FormControl>
                            <FormLabel>כתובות אימייל:</FormLabel>
                            {emailAddresses.map((email, index) => (
                                <HStack key={index} mt={2}>
                                    <Input
                                        placeholder="example@company.com"
                                        value={email.value}
                                        onChange={(e) => handleEmailChange(index, e.target.value)}
                                        isDisabled={isSending}
                                    />
                                    {emailAddresses.length > 1 && (
                                        <IconButton
                                            icon={<DeleteIcon />}
                                            onClick={() => handleRemoveEmail(index)}
                                            aria-label="Remove email"
                                            colorScheme="red"
                                            isDisabled={isSending}
                                        />
                                    )}
                                </HStack>
                            ))}
                            <Flex justify="flex-end" mt={2}>
                                <Button
                                    leftIcon={<AddIcon />}
                                    onClick={handleAddEmail}
                                    size="sm"
                                    isDisabled={isSending}
                                >
                                    הוסף אימייל
                                </Button>
                            </Flex>
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button
                        onClick={onClose}
                        mr={3}
                        isDisabled={isSending}
                    >
                        ביטול
                    </Button>
                    <Button
                        colorScheme="blue"
                        onClick={handleSendEmail}
                        isLoading={isSending}
                        loadingText="שולח..."
                    >
                        שלח
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
